import { createSelector } from 'reselect'

import { resolveTasks, isPersonalTask, testTaskDueDateFilter, filterTask } from '../utils/task';
import { ARCHIVED_TASK_GROUP, COMPLETED_TASK_GROUP } from 'constants/index';

const sortTasks = tasks => tasks.sort((t1, t2) => {
    const t1Order = (t1.order - 0);
    const t2Order = (t2.order - 0);
    return t1Order - t2Order;
});
const sortTasksByCompletionDate = tasks => tasks.sort((t1, t2) => new Date(t2.completed_on || new Date()).getTime() - new Date(t1.completed_on || new Date()).getTime());

const sortTasksByDueDate = tasks => tasks.sort((t1, t2) => {
    const t1DueDate = t1.due_date ? new Date(t1.due_date) : Number.POSITIVE_INFINITY;
    const t2DueDate = t2.due_date ? new Date(t2.due_date) : Number.POSITIVE_INFINITY;

    if (t1DueDate > t2DueDate) {
        return 1;
    } else if (t1DueDate < t2DueDate) {
        return -1;
    }

    const t1CreatedAt = t1.created_at ? new Date(t1.created_at) : Number.POSITIVE_INFINITY;
    const t2CreatedAt = t2.created_at ? new Date(t2.created_at) : Number.POSITIVE_INFINITY;

    if (t1CreatedAt > t2CreatedAt) {
        return 1;
    } else if (t1CreatedAt < t2CreatedAt) {
        return -1;
    }

    const t1NiceId = (t1.niceId || '').toLowerCase();
    const t2NiceId = (t2.niceId || '').toLowerCase();

    if (t1NiceId > t2NiceId) {
        return 1;
    } else if (t1NiceId < t2NiceId) {
        return -1;
    }

    const t1Name = (t1.name || '').toLowerCase();
    const t2Name = (t2.name || '').toLowerCase();

    if (t1Name > t2Name) {
        return 1;
    } else if (t1Name < t2Name) {
        return -1;
    }

    return 0;
});

const getTask = (tasks, id) => tasks.find(task => task.id === id || task.nice_id === id);
export const makeGetTask = () => createSelector(getTask, task => task);

const getSortedTasks = (tasks, ids) => sortTasks(tasks.filter(task => ids.includes(task.id) || ids.includes(task.nice_id)));
export const makeGetSortedTasks = () => createSelector(getSortedTasks, tasks => tasks);

const getNonArchivedTasks = tasks => sortTasks(tasks.filter(task => !task.archived));
export const makeGetNonArchivedTasks = () => createSelector(getNonArchivedTasks, tasks => tasks);

const getProjectTasks = (tasks, projectId) => tasks.filter(task => task.project === projectId && !task.task);
export const makeGetProjectTasks = () => createSelector(getProjectTasks, tasks => tasks);

const getOpenProjectTasks = (tasks, projectId) => tasks.filter(task => task.project === projectId && !task.task && !task.completed && !task.archived);
export const makeGetOpenProjectTasks = () => createSelector(getOpenProjectTasks, tasks => tasks);

const getSortedActiveProjectTasks = (tasks, projectId) => tasks
    .filter(task => task.project === projectId && !task.archived && !task.task && !task.completed)
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1);
export const makeGetSortedActiveProjectTasks = () => createSelector(getSortedActiveProjectTasks, tasks => tasks);

const getSortedArchivedProjectTasks = (tasks, projectId, allowedTasks) => tasks
    .filter(task => {
        const passedFilter = task.project === projectId && task.archived && !task.task;

        if (passedFilter && allowedTasks) {
            return allowedTasks.includes(task.id);
        }

        return passedFilter;
    })
    .sort((t1, t2) => new Date(t2.archived_on).getTime() - new Date(t1.archived_on).getTime());
export const makeGetSortedArchivedProjectTasks = () => createSelector(getSortedArchivedProjectTasks, tasks => tasks);

const getSortedCompletedProjectTasks = (tasks, projectId, allowedTasks) => tasks
    .filter(task => {
        const passedFilter = task.project === projectId && task.completed && !task.task && !task.archived;

        if (passedFilter && allowedTasks) {
            return allowedTasks.includes(task.id);
        }

        return passedFilter;
    })
    .sort((t1, t2) => new Date(t2.completed_on).getTime() - new Date(t1.completed_on).getTime());
export const makeGetSortedCompletedProjectTasks = () => createSelector(getSortedCompletedProjectTasks, tasks => tasks);

const getSortedCompletedProjectTasksAndSubtasks = (tasks, projectId, allowedTasks) => tasks
    .filter(task => {
        const passedFilter = task.project === projectId && task.completed && !task.archived;

        if (passedFilter && allowedTasks) {
            return allowedTasks.includes(task.id);
        }

        return passedFilter;
    })
    .sort((t1, t2) => new Date(t2.completed_on).getTime() - new Date(t1.completed_on).getTime());
export const makeGetSortedCompletedProjectTasksAndSubtasks = () => createSelector(getSortedCompletedProjectTasksAndSubtasks, tasks => tasks);

const getOpenScheduledProjectTasksAndSubtasks = (tasks, projectId) => tasks
    .filter(task => task.project === projectId && !task.archived && !task.completed && (task.due_date || task.start_date));
export const makeGetOpenScheduledProjectTasksAndSubtasks = () => createSelector(getOpenScheduledProjectTasksAndSubtasks, tasks => tasks);

const getMilestoneTasks = (tasks, milestoneId) => sortTasksByDueDate(tasks.filter(task => task.milestone === milestoneId && !task.archived && !task.project_archived))
export const makeGetMilestoneTasks = () => createSelector(getMilestoneTasks, tasks => tasks);

const getMilestoneActiveTasks = (tasks, milestoneId) => getMilestoneTasks(tasks, milestoneId).filter(task => !task.completed);
export const makeGetMilestoneActiveTasks = () => createSelector(getMilestoneActiveTasks, tasks => tasks);

const getSortedMilestoneActiveTasks = (tasks, milestoneId) => sortTasks(getMilestoneTasks(tasks, milestoneId).filter(task => !task.completed));
export const makeGetSortedMilestoneActiveTasks = () => createSelector(getSortedMilestoneActiveTasks, tasks => tasks);

const getMilestoneCompletedTasks = (tasks, milestoneId) => getMilestoneTasks(tasks, milestoneId).filter(task => task.completed);
export const makeGetMilestoneCompletedTasks = () => createSelector(getMilestoneCompletedTasks, tasks => tasks);

const getProjectTasksWithMilestone = (tasks, projectId) => tasks.filter(task => task.project === projectId && task.milestone);
export const makeGetProjectTasksWithMilestone = () => createSelector(getProjectTasksWithMilestone, tasks => tasks);

export const makeGetFilteredTask = () => createSelector(filterTask, task => task);

const getOpenUserTasks = (tasks, userId) => tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && (task.assignees || []).includes(userId))
    .sort((p1, p2) => p1.name > p2.name ? 1 : -1);
export const makeGetOpenUserTasks = () => createSelector(getOpenUserTasks, tasks => tasks);

const getSortedOpenUserTasks = (tasks, userId) => sortTasksByDueDate(tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && !task.task && (task.assignees || []).includes(userId)))
export const makeGetSortedOpenUserTasks = () => createSelector(getSortedOpenUserTasks, tasks => tasks);

const getSortedOpenUserTasksAndSubtasks = (tasks, userId) => sortTasksByDueDate(tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && (!userId || (task.assignees || []).includes(userId))))
export const makeGetSortedOpenUserTasksAndSubtasks = () => createSelector(getSortedOpenUserTasksAndSubtasks, tasks => tasks);

const getSortedCompletedUserTasksAndSubtasks = (tasks, userId) => sortTasksByCompletionDate(tasks
    .filter(task => !task.archived && !task.project_archived && task.completed && task.completed_by === userId));
export const makeGetSortedCompletedUserTasksAndSubtasks = () => createSelector(getSortedCompletedUserTasksAndSubtasks, tasks => tasks);

const getSortedOpenUserTasksAndSubtasksWithPersonal = (tasks, userId) => sortTasksByDueDate(tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && ((task.assignees || []).includes(userId) || isPersonalTask(task))));
export const makeGetSortedOpenUserTasksAndSubtasksWithPersonal = () => createSelector(getSortedOpenUserTasksAndSubtasksWithPersonal, tasks => tasks);

const getSortedCompletedUserTasksAndSubtasksWithPersonal = (tasks, userId) => sortTasksByCompletionDate(tasks
    .filter(task => !task.archived && !task.project_archived && task.completed && (task.completed_by === userId || isPersonalTask(task))));
export const makeGetSortedCompletedUserTasksAndSubtasksWithPersonal = () => createSelector(getSortedCompletedUserTasksAndSubtasksWithPersonal, tasks => tasks);

const getSortedOpenScheduledUserTasks = (tasks, userId) => sortTasksByDueDate(tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && !task.task && (task.due_date || task.start_date) && (!userId || (task.assignees || []).includes(userId))))
export const makeGetSortedOpenScheduledUserTasks = () => createSelector(getSortedOpenScheduledUserTasks, tasks => tasks);

const getSortedOpenScheduledUserTasksAndSubtasks = (tasks, userId) => sortTasksByDueDate(tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && (task.due_date || task.start_date) && (!userId || (task.assignees || []).includes(userId))))
export const makeGetSortedOpenScheduledUserTasksAndSubtasks = () => createSelector(getSortedOpenScheduledUserTasksAndSubtasks, tasks => tasks);

const getSortedCompletedUserTasks = (tasks, userId) => sortTasksByCompletionDate(tasks
    .filter(task => !task.archived && !task.project_archived && !task.task && task.completed && (!userId || task.completed_by === userId)));
export const makeGetSortedCompletedUserTasks = () => createSelector(getSortedCompletedUserTasks, tasks => tasks);

const getSortedOpenTasks = (tasks, projectIds) => sortTasksByDueDate(tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && !task.task && (!projectIds || projectIds.includes(task.project))));

export const makeGetSortedOpenTasks = () => createSelector(getSortedOpenTasks, tasks => tasks);

const getSortedOpenTasksAndSubtasks = (tasks, projectIds) => sortTasksByDueDate(tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && (!projectIds || projectIds.includes(task.project))));
export const makeGetSortedOpenTasksAndSubtasks = () => createSelector(getSortedOpenTasksAndSubtasks, tasks => tasks);

const getSortedOpenUserProjectTasks = (tasks, userId, projectId) => sortTasksByDueDate(tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && !task.task && task.project === projectId && (task.assignees || []).includes(userId)))
export const makeGetSortedOpenUserProjectTasks = () => createSelector(getSortedOpenUserProjectTasks, tasks => tasks);

const getOpenUserTasksCount = (tasks, userId) => tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && (task.assignees || []).includes(userId)).length;
export const makeGetOpenUserTasksCount = () => createSelector(getOpenUserTasksCount, tasksCount => tasksCount);

const getCompletedUserTasksCount = (tasks, userId) => tasks
    .filter(task => !task.archived && !task.project_archived && !task.task && task.completed && task.completed_by === userId).length;
export const makeGetCompletedUserTasksCount = () => createSelector(getCompletedUserTasksCount, tasksCount => tasksCount);

const getOverdueUserTasksCount = (tasks, userId) => tasks
    .filter(task => !task.archived && !task.project_archived && !task.completed && task.due_date && (task.assignees || []).includes(userId) && new Date(task.due_date) < new Date()).length;
export const makeGetOverdueUserTasksCount = () => createSelector(getOverdueUserTasksCount, tasksCount => tasksCount);

const getTaskGroupTasks = (tasks, taskGroupId, projectId, allowedTasks) => sortTasks(tasks
    .filter(task => {
        const passedFilter = task.task_group === taskGroupId && !task.archived;

        if (passedFilter && allowedTasks) {
            return allowedTasks.includes(task.id);
        }

        return passedFilter;
    }));
export const makeGetTaskGroupTasks = () => createSelector(getTaskGroupTasks, tasks => tasks);

const getTaskGroupOpenTasks = (tasks, taskGroupId, projectId, allowedTasks) => sortTasks(tasks
    .filter(task => {
        const passedFilter = task.task_group === taskGroupId && !task.archived && !task.completed;

        if (passedFilter && allowedTasks) {
            return allowedTasks.includes(task.id);
        }

        return passedFilter;
    }));
export const makeGetTaskGroupOpenTasks = () => createSelector(getTaskGroupOpenTasks, tasks => tasks);

const getTaskGroupOpenTasksWithoutMilestone = (tasks, taskGroupId) => sortTasks(tasks
    .filter(task => {
        const passedFilter = task.task_group === taskGroupId && !task.archived && !task.completed && !task.milestone;

        return passedFilter;
    }));
export const makeGetTaskGroupOpenTasksWithoutMilestone = () => createSelector(getTaskGroupOpenTasksWithoutMilestone, tasks => tasks);

const getProjectCompletedTasksWithoutMilestone = (tasks, projectId) => sortTasks(tasks
    .filter(task => {
        const passedFilter = task.project === projectId && !task.archived && task.completed && !task.milestone && !task.task;

        return passedFilter;
    }));
export const makeGetProjectCompletedTasksWithoutMilestone = () => createSelector(getProjectCompletedTasksWithoutMilestone, tasks => tasks);

const getTaskGroupOpenUnscheduledTasks = (tasks, taskGroupId) => sortTasks(tasks
    .filter(task => {
        const passedFilter = task.task_group === taskGroupId && !task.archived && !task.completed && !(task.start_date || task.due_date);

        return passedFilter;
    }));
export const makeGetTaskGroupOpenUnscheduledTasks = () => createSelector(getTaskGroupOpenUnscheduledTasks, tasks => tasks);

const getTransformedTaskGroupTasks = (tasks, taskGroupId, projectId, allowedTasks) => {
    const taskGroupTasks = getTaskGroupTasks(tasks, taskGroupId, projectId, allowedTasks);
    const transformed = {};

    taskGroupTasks.forEach((item, i) => {
        if (item) {
            transformed[i] = item;
        }
    });
    return transformed;
}
export const makeGetTransformedTaskGroupTasks = () => createSelector(getTransformedTaskGroupTasks, tasks => tasks);

const getTaskSubtasks = (tasks, taskId) => sortTasks(tasks.filter(task => task.task === taskId));
export const makeGetTaskSubtasks = () => createSelector(getTaskSubtasks, tasks => tasks);

const getTaskActiveSubtasks = (tasks, taskId) => sortTasks(tasks.filter(task => !task.completed && task.task === taskId));
export const makeGetTaskActiveSubtasks = () => createSelector(getTaskActiveSubtasks, tasksCount => tasksCount);

const getTaskCompletedSubtasks = (tasks, taskId) => sortTasksByCompletionDate(tasks.filter(task => task.completed && task.task === taskId));
export const makeGetTaskCompletedSubtasks = () => createSelector(getTaskCompletedSubtasks, tasksCount => tasksCount);

const getTaskSubtasksCount = (tasks, taskId) => tasks.filter(task => task.task === taskId).length;
export const makeGetTaskSubtasksCount = () => createSelector(getTaskSubtasksCount, tasksCount => tasksCount);

const getTaskCompletedSubtasksCount = (tasks, taskId) => tasks.filter(task => task.completed && task.task === taskId).length;
export const makeGetTaskCompletedSubtasksCount = () => createSelector(getTaskCompletedSubtasksCount, tasksCount => tasksCount);

const getProjectsFromTasks = tasks => [...new Set(tasks.map(task => task.project))];
export const makeGetProjectsFromTasks = () => createSelector(getProjectsFromTasks, projects => projects);

const getOpenTasks = tasks => tasks.filter(task => !task.archived && !task.project_archived && !task.completed);
export const makeGetOpenTasks = () => createSelector(getOpenTasks, projects => projects);

const getCompletedTasks = tasks => tasks.filter(task => !task.archived && !task.project_archived && task.completed);
export const makeGetCompletedTasks = () => createSelector(getCompletedTasks, projects => projects);

const getResolvedTasks = (taskIds, tasks) => {
    const resolvedTasks = resolveTasks(taskIds);

    if (Array.isArray(resolvedTasks)) {
        return resolvedTasks;
    }

    return [];
}
export const makeGetResolvedTasks = () => createSelector(getResolvedTasks, tasks => tasks);

const getResolvedOpenTasks = (taskIds, tasks) => getResolvedTasks(taskIds, tasks)
    .filter(task => !task.archived && !task.project_archived && !task.completed);
export const makeGetResolvedOpenTasks = () => createSelector(getResolvedOpenTasks, tasks => tasks);

const getResolvedCompletedTasks = (taskIds, tasks) => getResolvedTasks(taskIds, tasks)
    .filter(task => !task.archived && !task.project_archived && task.completed);
export const makeGetResolvedCompletedTasks = () => createSelector(getResolvedCompletedTasks, tasks => tasks);

const getTasksFetchedByGroups = (fetchInfo, taskGroupsIds) => taskGroupsIds.every(id => id === 'archived' || (fetchInfo.taskGroup[id] && fetchInfo.taskGroup[id].fetched));
export const makeGetTasksFetchedByGroups = () => createSelector(getTasksFetchedByGroups, fetched => fetched);

const getTasksFilteredByDueDate = (tasks, dueDateFilter) => tasks.filter(task => testTaskDueDateFilter(dueDateFilter, task));
export const makeGetTasksFilteredByDueDate = () => createSelector(getTasksFilteredByDueDate, tasks => tasks);

const getTasksFilteredByCompletedOn = (tasks, rangeFilter) => tasks.filter(task => task.completed_on && new Date(task.completed_on) >= rangeFilter.start && new Date(task.completed_on) <= rangeFilter.end);
export const makeGetTasksFilteredByCompletedOn = () => createSelector(getTasksFilteredByCompletedOn, tasks => tasks);

const getTasksByMeetingId = (tasks, meetingId) => sortTasksByDueDate(tasks.filter(task => task?.meeting_ids && !isPersonalTask(task) && task.meeting_ids.includes(meetingId)));
export const makeGetTasksByMeetingId = () => createSelector(getTasksByMeetingId, tasks => tasks);

const getPersonalTasksByMeetingId = (tasks, meetingId) => sortTasksByDueDate(tasks.filter(task => task?.meeting_ids && isPersonalTask(task) && task.meeting_ids.includes(meetingId)));
export const makeGetPersonalTasksByMeetingId = () => createSelector(getPersonalTasksByMeetingId, tasks => tasks);

const getTaskGroupsAndTasks = (taskGroups, tasks, projectId, allowedTasks, withCompleted, withArchived) => {
    const result = [];

    if (withCompleted) {
        taskGroups = [...taskGroups, {...COMPLETED_TASK_GROUP, project: projectId }];
    }

    if (withArchived) {
        taskGroups = [...taskGroups, {...ARCHIVED_TASK_GROUP, project: projectId }];
    }

    taskGroups.forEach(taskGroup => {
        taskGroup = {...taskGroup};

        result.push(taskGroup);

        let taskGroupTasks = [];

        if (taskGroup.taskIds) {
            taskGroupTasks = getResolvedTasks(taskGroup.taskIds, tasks);
        } else if (taskGroup) {
            if (taskGroup.type === 'archived') {
                taskGroupTasks = getSortedArchivedProjectTasks(tasks, projectId, allowedTasks);
            } else if (taskGroup.type === 'completed') {
                taskGroupTasks = getSortedCompletedProjectTasks(tasks, projectId, allowedTasks);
            } else {
                taskGroupTasks = getTaskGroupOpenTasks(tasks, taskGroup.id, projectId, allowedTasks);
            }
        }

        taskGroup.tasks = taskGroupTasks;
        // result.push(...taskGroupTasks);
    });

    return result;
};
export const makeGetTaskGroupsAndTasks = () => createSelector(getTaskGroupsAndTasks, tasks => tasks);

const getMovedTaskOrder = (tasks, taskId, targetTaskGroupId, targetTaskId, isBefore) => {
    if (['archived', 'completed'].includes(targetTaskGroupId)) {
        return undefined;
    }

    const taskGroupTasks = sortTasks(tasks.filter(task => task.task_group === targetTaskGroupId && !task.archived && !task.completed && !task.removed && !task.task));
    const currentTask = taskGroupTasks.find(task => task.id === taskId);
    const targetTask = taskGroupTasks.find(task => task.id === targetTaskId);
    const currentTaskIndex = taskGroupTasks.indexOf(currentTask);
    const targetTaskIndex = taskGroupTasks.indexOf(targetTask);
    const insertAfter = (currentTaskIndex <= 0 && !isBefore) || (currentTaskIndex >= 0 && currentTaskIndex < targetTaskIndex);
    let otherTaskOrder = 100;

    if (insertAfter) {
        const otherTask = taskGroupTasks[targetTaskIndex + 1];
        if (taskId && otherTask?.id === taskId) {
            return otherTask.order;
        }

        otherTaskOrder = otherTask?.order ?? (targetTask?.order ? targetTask.order + 100 : 0);
    } else {
        const firstTask = taskGroupTasks[0];
        const otherTask = taskGroupTasks[targetTaskIndex - 1];
        if (taskId && otherTask?.id === taskId) {
            return otherTask.order;
        }

        otherTaskOrder = otherTask?.order || Math.max(firstTask?.order && (firstTask?.order - .1), 0) || 0;
    }

    return ((targetTask?.order ?? 0) + otherTaskOrder) / 2;
};
export const makeGetMovedTaskOrder = () => createSelector(getMovedTaskOrder, tasks => tasks);

const getPersonalTasks = tasks => tasks.filter(task => isPersonalTask(task));
export const makeGetPersonalTasks = () => createSelector(getPersonalTasks, tasks => tasks);

const getOpenPersonalTasks = (tasks, includedTasks) => tasks
    .filter(task => includedTasks.includes(task?.id) || (!task.removed && isPersonalTask(task) && !task.completed && !task.archived))
    .sort((t1, t2) => {
        const t1CreatedAt = t1.created_at ? new Date(t1.created_at) : Number.POSITIVE_INFINITY;
        const t2CreatedAt = t2.created_at ? new Date(t2.created_at) : Number.POSITIVE_INFINITY;

        if (t1CreatedAt > t2CreatedAt) {
            return 1;
        } else if (t1CreatedAt < t2CreatedAt) {
            return -1;
        }

        return 0;
    });
export const makeGetOpenPersonalTasks = () => createSelector(getOpenPersonalTasks, tasks => tasks);

const getCompletedPersonalTasks = tasks => tasks.filter(task => !task.removed && isPersonalTask(task) && task.completed && !task.archived);
export const makeGetCompletedPersonalTasks = () => createSelector(getCompletedPersonalTasks, tasks => tasks);

const getTaskGroupOrStaticTasks = (tasks, taskIds, taskGroupId, taskGroupType, projectId, allowedTasks) => {
    let taskGroupTasks = [];

    if (taskIds) {
        taskGroupTasks = getResolvedTasks(taskIds, tasks);
    } else {
        if (taskGroupType && projectId) {
            if (taskGroupType === 'archived') {
                taskGroupTasks = getSortedArchivedProjectTasks(tasks, projectId, allowedTasks);
            }

            if (taskGroupType === 'completed') {
                taskGroupTasks = getSortedCompletedProjectTasks(tasks, projectId, allowedTasks);
            }
        } else if (taskGroupId) {
            taskGroupTasks = getTaskGroupOpenTasks(tasks, taskGroupId, projectId, allowedTasks);
        }
    }

    return taskGroupTasks;
};
export const makeGetTaskGroupOrStaticTasks = () => createSelector(getTaskGroupOrStaticTasks, taskIds => taskIds);

const getTaskGroupOrStaticTasksIds = (tasks, taskIds, taskGroupId, taskGroupType, projectId, allowedTasks) => {
    return getTaskGroupOrStaticTasks(tasks, taskIds, taskGroupId, taskGroupType, projectId, allowedTasks).map(task => task.id);
};
export const makeGetTaskGroupOrStaticTasksIds = () => createSelector(getTaskGroupOrStaticTasksIds, taskIds => taskIds);
